import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { flagsApi } from '../services/api';
import { RefreshCw, ToggleLeft } from 'lucide-react';

const LEVEL_LABEL = { nav: 'Módulo', feat: 'Funcionalidad' };
const LEVEL_CLS = {
  nav:  'bg-blue-100 text-blue-700',
  feat: 'bg-violet-100 text-violet-700',
};
const GROUP_ORDER = ['General', 'CRM', 'Ventas', 'IA'];

export default function AdminFeatureFlags() {
  const [defs, setDefs] = useState([]);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = async () => {
    setLoading(true);
    setMsg(null);
    try {
      const data = await flagsApi.definitions();
      setDefs(data);
    } catch (e) {
      setMsg({ error: true, text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const toggle = async (key, enabled) => {
    setSaving(key);
    setMsg(null);
    try {
      await flagsApi.update(key, enabled);
      setDefs((prev) => prev.map((f) => f.key === key ? { ...f, enabled } : f));
    } catch (e) {
      setMsg({ error: true, text: e.message });
    } finally {
      setSaving(null);
    }
  };

  const byGroup = {};
  for (const f of defs) {
    (byGroup[f.group] ||= []).push(f);
  }
  const groups = GROUP_ORDER.filter((g) => byGroup[g]);

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ToggleLeft className="w-5 h-5 text-brand-600" />
            <h1 className="text-2xl font-bold text-ink-900">Funciones activas</h1>
          </div>
          <p className="text-ink-500 text-sm">
            Activa o desactiva módulos y funcionalidades para usuarios no-admin.
            El administrador siempre ve todo, independientemente de estos ajustes.
          </p>
        </div>
        <button
          onClick={cargar}
          className="p-2 hover:bg-ink-100 rounded transition"
          title="Recargar"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 text-ink-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {msg && (
        <div className={`px-4 py-3 rounded-lg text-sm ${msg.error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
          {msg.text}
        </div>
      )}

      {loading && !defs.length ? (
        <div className="text-ink-400 text-sm py-8 text-center">Cargando configuración…</div>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <Card key={group} className="overflow-hidden">
              <div className="px-5 py-3 bg-ink-50 border-b border-ink-100">
                <h2 className="font-semibold text-ink-700 text-sm">{group}</h2>
              </div>
              <div className="divide-y divide-ink-100">
                {byGroup[group].map((f) => (
                  <div key={f.key} className="flex items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${LEVEL_CLS[f.level]}`}>
                        {LEVEL_LABEL[f.level]}
                      </span>
                      <span className="text-sm text-ink-800 truncate">{f.label}</span>
                      <span className="text-xs text-ink-300 font-mono hidden sm:inline">{f.key}</span>
                    </div>
                    <Switch
                      checked={f.enabled}
                      disabled={saving === f.key}
                      onCheckedChange={(v) => toggle(f.key, v)}
                      className="shrink-0 ml-4"
                    />
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
